import { Box, InputAdornment, Typography } from "@mui/material"
import { If, Else } from "lib/@components/switch"
import { BoundTextField } from "lib/@components/binding/bound-components"
import { convertToMinMaxInteger } from "lib/number"
import { useEffect, useMemo, useRef } from "react"
import noop from "lib/noop"
import { SummaryDataChanged } from "event-definitions"
import { prevent } from "lib/prevent"
import { BoundSimpleCombo } from "lib/@components/combos/boundSimpleCombo"
import { useBoundValue } from "lib/@components/binding/use-bound-value"
import { warningSnackbar } from "lib/snackbar/warning-snackbar"
import { RegimeDraftLockTooltip } from "routes/regime/regime-draft-locking/regime-draft-lock-tooltip"
import { frequencyCode } from "routes/schedule/tasks/frequency-override"

/**
 * The interval and period inputs. They are bound to whatever `FrequencyEditor` puts in context as
 * the target: the task itself in the master editors, a draft in the tailoring editor. So the
 * normalising effects below can settle the displayed value freely without touching a tailoring
 * payload, and `commit` decides what (if anything) the payload should end up holding.
 */
export function FrequencyFields({ cancel = noop, onClick = noop, readOnly, sx, markEdited = noop, commit = noop }) {
    const me = useRef()
    const time = useRef(0)
    const input = useRef()
    const [frequency, setFrequency] = useBoundValue("frequency.interval", "")
    const [period, setPeriod] = useBoundValue("frequency.period", "")
    const [code, setCode] = useBoundValue("frequency.code", "")
    const [click, handleBlur] = useMemo(
        () => [
            function click() {
                if (readOnly) return
                time.current = Date.now()
                onClick()
                setTimeout(() => input.current?.focus())
            },
            function handleBlur() {
                if (readOnly) return
                if (!frequency || !+frequency) {
                    setFrequency("0")
                    setPeriod("Unknown")
                }
                if (Date.now() - time.current > 70) {
                    cancel()
                }
            },
        ],
        [readOnly, onClick, frequency, setPeriod, setFrequency, cancel]
    )
    useEffect(normalisePeriod, [period, frequency, setPeriod])
    useEffect(syncCode, [frequency, period, code, setCode])
    useEffect(clampFrequency, [frequency, setFrequency])
    // Backstop for the effects above: whatever value they settle on gets committed, but `commit`
    // writes nothing until `markEdited` says a person moved the interval or the period.
    useEffect(commitFrequency, [frequency, period, commit])
    return (
        <Box ref={me} onBlur={handleBlur}>
            <RegimeDraftLockTooltip>
                <BoundTextField
                    variant="outlined"
                    label="Frequency"
                    data-cy="frequency-input"
                    onClick={prevent(noop)}
                    fullWidth={false}
                    onChanged={frequencyEdited}
                    sideEffectsOnBlur
                    inputRef={input}
                    onKeyDown={preventNonNumeric}
                    sx={{ width: 122, m: 0, p: 0, ...sx }}
                    InputProps={{
                        readOnly,
                        sx: { pr: 0.2 },
                        endAdornment: (
                            <If value={!!frequency && +frequency > 0}>
                                <InputAdornment position="end">
                                    <RegimeDraftLockTooltip disableTitle>
                                        <BoundSimpleCombo
                                            onChanged={frequencyEdited}
                                            aria-label="Period"
                                            readOnly={readOnly}
                                            options={{
                                                Hour: "Hour",
                                                Day: "Day",
                                                Week: "Week",
                                                Month: "Month",
                                                Year: "Year",
                                            }}
                                            onClick={click}
                                            field="frequency.period"
                                            data-cy="frequency-period"
                                        />
                                    </RegimeDraftLockTooltip>
                                </InputAdornment>
                                <Else>
                                    <InputAdornment position="end">
                                        <Typography
                                            variant="body2"
                                            sx={{ fontWeight: "bold", pr: 2, fontSize: 13, color: "text.secondary" }}
                                        >
                                            Undefined
                                        </Typography>
                                    </InputAdornment>
                                </Else>
                            </If>
                        ),
                    }}
                    transformOutOnBlur={convertToMinMaxInteger(0, 1200)}
                    field="frequency.interval"
                    size="small"
                />
            </RegimeDraftLockTooltip>
        </Box>
    )

    // The one place that says "a person changed this". Both inputs call it after they have written
    // the new value, so committing here lands the override before the change chain runs. The combo
    // also calls it when its menu merely opens, which is harmless: an unmoved draft still matches
    // what the task inherits, so `commit` writes nothing.
    function frequencyEdited(value) {
        markEdited()
        commit()
        SummaryDataChanged.raiseOnce(value)
    }

    function commitFrequency() {
        commit()
    }

    function normalisePeriod() {
        if (!!frequency && frequency > 0 && (!period || period === "Unknown")) {
            setPeriod("Month")
        }
        if (!frequency || +frequency === 0) setPeriod("Unknown")
    }

    function syncCode() {
        if (frequency !== undefined && period !== undefined) {
            const newCode = frequencyCode(frequency, period)
            if (code !== newCode) {
                setCode(newCode)
            }
        }
    }

    function clampFrequency() {
        if (frequency > 1200) {
            setFrequency(1200)
            warningSnackbar("Frequency cannot exceed 1200")
        }
    }

    function preventNonNumeric(e) {
        const allowedKeys = ["Backspace", "Delete", "ArrowLeft", "ArrowRight", "Tab", "Enter", "Escape"]
        const isNumber = /^[0-9]$/.test(e.key)

        if (!isNumber && !allowedKeys.includes(e.key) && !e.ctrlKey && !e.metaKey) {
            e.preventDefault()
        }
    }
}
